import React, { useEffect, useRef, useState } from 'react';

import { ArrowRightIcon, CaretRightIcon, XIcon } from '@phosphor-icons/react';

import {
  MPFButton,
  MPFIconButton,
} from 'volto-ministerio-publico-federal/components/UI';
import { MenuLink } from './Menu';
import { flattenToAppURL } from '@plone/volto/helpers';
import { megaMenuPanelId, menuItemKey, type MenuItem } from './mainMenu';
import { resolveMenuIcon } from './menuIcons';

interface MegaMenuColumnProps {
  items: MenuItem[];
  activeKey?: string;
  /* Id da coluna seguinte, alvo do `aria-controls` da seta. */
  nextColumnId: string;
  onSelect: (item: MenuItem, viaKeyboard: boolean) => void;
  onNavigate: (item: MenuItem) => void;
}

const MegaMenuRowLabel: React.FC<{ entry: MenuItem }> = ({ entry }) => (
  <span className="mpf-topbar__mega-row-text">
    <span className="mpf-topbar__mega-row-label">{entry.title}</span>
    {entry.teaser ? (
      <span className="mpf-topbar__mega-row-teaser">{entry.teaser}</span>
    ) : null}
  </span>
);

const rowClassName = (entry: MenuItem, modifiers: string[] = []): string =>
  [
    'mpf-topbar__mega-row',
    ...modifiers,
    entry.highlight ? 'mpf-topbar__mega-row--highlight' : '',
  ]
    .filter(Boolean)
    .join(' ');

/*
Item com filhos vira duas ações irmãs: o texto é link para a página do item e
a seta abre o próximo nível na coluna seguinte (padrão disclosure do APG, sem
`role="menu"`). Item sem filhos é só o link.
*/
const MegaMenuColumn: React.FC<MegaMenuColumnProps> = ({
  items,
  activeKey,
  nextColumnId,
  onSelect,
  onNavigate,
}) => (
  <ul className="mpf-topbar__mega-list">
    {items.map((entry) => {
      const key = menuItemKey(entry);
      const hasChildren = (entry.items?.length ?? 0) > 0;
      const isActive = activeKey === key;

      return (
        <li key={key} className="mpf-topbar__mega-row-item">
          {hasChildren ? (
            <div
              className={rowClassName(entry, [
                'mpf-topbar__mega-row--split',
                isActive ? 'mpf-topbar__mega-row--active' : '',
              ])}
            >
              <MenuLink
                node={entry}
                className="mpf-topbar__mega-row-link"
                onClick={() => onNavigate(entry)}
              >
                <MegaMenuRowLabel entry={entry} />
              </MenuLink>
              <MPFButton
                variant="unstyled"
                className="mpf-topbar__mega-row-toggle"
                aria-expanded={isActive}
                aria-controls={isActive ? nextColumnId : undefined}
                // Sem texto no botão, o `title` vira o nome acessível: a dica
                // visual e o que o leitor de tela anuncia são o mesmo texto
                // (WCAG 2.5.3). Somar um `aria-label` igual faria o Chrome
                // expor o `title` também como descrição, lida em dobro.
                title={`Acessar subitens de ${entry.title}`}
                // `detail` 0 = ativado pelo teclado (Enter/Espaço), não pelo mouse.
                onClick={(event) => onSelect(entry, event.detail === 0)}
              >
                <CaretRightIcon
                  className="mpf-topbar__mega-chevron"
                  aria-hidden="true"
                />
              </MPFButton>
            </div>
          ) : (
            <MenuLink
              node={entry}
              className={rowClassName(entry, ['mpf-topbar__mega-row--leaf'])}
              onClick={() => onNavigate(entry)}
            >
              <MegaMenuRowLabel entry={entry} />
            </MenuLink>
          )}
        </li>
      );
    })}
  </ul>
);

export interface MegaMenuProps {
  item: MenuItem;
  onClose: () => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
}

export const MegaMenu: React.FC<MegaMenuProps> = ({
  item,
  onClose,
  panelRef,
}) => {
  const [activePath, setActivePath] = useState<MenuItem[]>([]);
  const closeRef = useRef<HTMLButtonElement>(null);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);
  const focusNextColumnRef = useRef(false);

  const itemId = menuItemKey(item);
  const panelId = megaMenuPanelId(itemId);
  const titleId = `${panelId}-title`;
  const columnId = (index: number) => `${panelId}-col-${index}`;
  const SectionIcon = resolveMenuIcon(item.icon);

  const columns = [item, activePath[0], activePath[1]]
    .map((parent) => ({
      key: parent ? menuItemKey(parent) : '',
      parent,
      items: parent?.items ?? [],
    }))
    .filter((column) => column.items.length > 0);

  useEffect(() => {
    setActivePath([]);
  }, [itemId]);

  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  /* Aberta pelo teclado, a coluna nova recebe o foco no primeiro item: sem
     isso, chegar ao submenu exigiria percorrer com Tab a coluna atual toda. */
  useEffect(() => {
    if (!focusNextColumnRef.current) return;
    focusNextColumnRef.current = false;
    columnRefs.current[activePath.length]
      ?.querySelector<HTMLElement>('a, button')
      ?.focus({ preventScroll: true });
  }, [activePath]);

  /* Mudar de rota já fecha o painel (efeito de `pathname` do Header); o link
     para a própria página atual não muda a rota, então fecha aqui. */
  const handleNavigate = (entry: MenuItem) => {
    const target = flattenToAppURL(entry.url ?? '');
    if (target === window.location.pathname) onClose();
  };

  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return undefined;

    const medir = () => {
      const teto = Number.parseFloat(getComputedStyle(viewport).maxHeight);
      const altura = content.offsetHeight;
      setContentHeight(Number.isFinite(teto) ? Math.min(altura, teto) : altura);
    };
    medir();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(medir);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  /* Ícone + título; vira link quando o editor preencheu "Link do título da
     seção". O painel fecha ao navegar pelo efeito de `pathname` do Header. */
  const headingContent = (
    <>
      <SectionIcon className="mpf-topbar__mega-icon" aria-hidden="true" />
      <span className="mpf-topbar__mega-title" id={titleId}>
        {item.title}
      </span>
    </>
  );

  return (
    <>
      {}
      <div className="mpf-topbar__mega-scrim" aria-hidden="true" />

      <div
        ref={panelRef}
        id={panelId}
        className="mpf-topbar__mega"
        role="region"
        aria-labelledby={titleId}
      >
        <div className="mpf-topbar__mega_container">
          <div className="mpf-topbar__mega-header">
            <div className="mpf-topbar__mega-headline">
              <h2 className="mpf-topbar__mega-heading">
                {item.headerLink ? (
                  <MenuLink
                    node={{ title: item.title, url: item.headerLink }}
                    className="mpf-topbar__mega-heading-link"
                  >
                    {headingContent}
                    <ArrowRightIcon
                      className="mpf-topbar__mega-heading-arrow"
                      aria-hidden="true"
                    />
                  </MenuLink>
                ) : (
                  headingContent
                )}
              </h2>
              {item.teaser ? (
                <p className="mpf-topbar__mega-teaser">{item.teaser}</p>
              ) : null}
            </div>

            <MPFIconButton
              variant="unstyled"
              ref={closeRef}
              className="mpf-topbar__mega-close"
              aria-label={`Fechar o menu ${item.title}`}
              onClick={onClose}
              icon={<XIcon className="mpf-topbar__mega-close-icon" />}
            />
          </div>

          <div
            ref={viewportRef}
            className="mpf-topbar__mega-viewport"
            style={
              contentHeight === null ? undefined : { height: contentHeight }
            }
          >
            <div
              ref={contentRef}
              className={`mpf-topbar__mega-columns mpf-topbar__mega-columns--cols-${columns.length}`}
            >
              {columns.map((column, index) => (
                <div
                  key={column.key}
                  ref={(node) => {
                    columnRefs.current[index] = node;
                  }}
                  id={columnId(index)}
                  className="mpf-topbar__mega-col"
                  {...(index > 0 && column.parent
                    ? {
                        role: 'group',
                        'aria-label': `Subitens de ${column.parent.title}`,
                      }
                    : {})}
                >
                  <MegaMenuColumn
                    items={column.items}
                    activeKey={
                      activePath[index]
                        ? menuItemKey(activePath[index])
                        : undefined
                    }
                    nextColumnId={columnId(index + 1)}
                    onSelect={(entry, viaKeyboard) => {
                      focusNextColumnRef.current = viaKeyboard;
                      setActivePath((prev) => [...prev.slice(0, index), entry]);
                    }}
                    onNavigate={handleNavigate}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MegaMenu;
